import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { formatNumber, formatMetric, getClassNames } from '../../../utils/util';

import Button from '../../common/button/Button';
import ListingCard from '../../common/listing/listing-card/ListingCard';
import ProductListing from '../../common/listing/product-listing/ProductListing';
import BuyerListing from '../../common/listing/buyer-listing/BuyerListing';
import PostContainer from '../../../containers/common/post/Post.container';

import { ReactComponent as SwitchViewIcon } from '../../../resources/icons/switch-view.svg';
import { ReactComponent as SwitchViewAlternateIcon } from '../../../resources/icons/switch-view-alternate.svg';
import { ReactComponent as StarFilledIcon } from '../../../resources/icons/star-filled.svg';
import { ReactComponent as StarEmptyIcon } from '../../../resources/icons/star-empty.svg';
import { ReactComponent as DoubleArrowIcon } from '../../../resources/icons/arrow-double.svg';

import './dashboard.scss';

const Dashboard = ({
  featuredListings,
  bookmarkedListings,
  productListings,
  posts,
  reactions,
  adminRole,
  isLastPostLoaded,
  updateBookmark,
  viewPosts,
  currencyCode,
}) => {
  const [bookmarkedListingsMiniView, setBookmarkedListingsMiniView] = useState(true);
  const [productListingsMiniView, setProductListingsMiniView] = useState(true);

  const navigate = useNavigate();

  const renderFeaturedListings = () => {
    if (featuredListings.length === 0) {
      return <p className="dashboard__empty-text">No Featured Listings</p>;
    }

    return featuredListings?.map((listing) => {
      const {
        productExternalReference,
        name,
        singleUnitPrice,
        singleUnitSize,
        singleUnitMetric,
        linkedMediaExternalReference,
        bookedMarked,
        currencyCode,
      } = listing;

      const description = `${currencyCode} ${formatNumber(singleUnitPrice)}/${formatNumber(
        singleUnitSize
      )} ${formatMetric(singleUnitMetric)}`;

      return (
        <ListingCard
          key={productExternalReference}
          title={name}
          description={description}
          images={[linkedMediaExternalReference]}
          bookmarked={bookedMarked}
          bookmarkListing={() => updateBookmark(listing, 'seller')}
          navigateToProduct={() => navigate(`/market/product-listing/${listing.productExternalReference}`)}
        />
      );
    });
  };

  const renderMiniViewHeaders = () => (
    <section className="dashboard__mini-headers">
      <p>Product</p>
      <p>{currencyCode} Price/Product Unit</p>
      <p>Available Date</p>
      <span />
    </section>
  );

  const renderBookmarkedListings = () => {
    if (bookmarkedListings.length === 0) {
      return <p className="dashboard__empty-text">No Bookmarks</p>;
    }

    return (
      <>
        {bookmarkedListingsMiniView && renderMiniViewHeaders()}
        {bookmarkedListings.map((listing) => {
          const actions = (
            <div className={getClassNames('dashboard__listing-actions', { mini: bookmarkedListingsMiniView })}>
              <div
                className={getClassNames('dashboard__listing-actions__action', {
                  bookmarked: true,
                })}
                onClick={() => updateBookmark(listing, listing.isBuyer ? 'buyer' : 'seller')}
              >
                <p>Bookmark This</p>
                <StarFilledIcon className="dashboard__listing-actions__action__icon" />
              </div>
              <div
                className="dashboard__listing-actions__action"
                onClick={() => navigate(`/market/product-listing/${listing.productExternalReference}`)}
              >
                <p>View Product Page</p>
                <DoubleArrowIcon className="dashboard__listing-actions__action__icon" />
              </div>
            </div>
          );

          if (listing.isBuyer) {
            return (
              <BuyerListing
                key={listing.productExternalReference}
                listing={listing}
                actions={actions}
                mini={bookmarkedListingsMiniView}
              />
            );
          } else {
            return (
              <ProductListing
                key={listing.productExternalReference}
                listing={listing}
                actions={actions}
                mini={bookmarkedListingsMiniView}
              />
            );
          }
        })}
      </>
    );
  };

  const renderProductListings = () => {
    if (productListings.length === 0) {
      return <p className="dashboard__empty-text">No Product Listings</p>;
    }

    return (
      <>
        {productListingsMiniView && renderMiniViewHeaders()}
        {productListings.map((listing) => {
          const actions = (
            <div className={getClassNames('dashboard__listing-actions', { mini: productListingsMiniView })}>
              <div
                className={getClassNames('dashboard__listing-actions__action', {
                  bookmarked: listing.bookmarked,
                })}
                onClick={() => updateBookmark(listing, 'seller')}
              >
                <p>Bookmark This</p>
                {listing.bookmarked ? (
                  <StarFilledIcon className="dashboard__listing-actions__action__icon" />
                ) : (
                  <StarEmptyIcon className="dashboard__listing-actions__action__icon" />
                )}
              </div>
              <div
                className="dashboard__listing-actions__action"
                onClick={() => navigate(`/market/product-listing/${listing.productExternalReference}`)}
              >
                <p>View Product Page</p>
                <DoubleArrowIcon className="dashboard__listing-actions__action__icon" />
              </div>
            </div>
          );

          return (
            <ProductListing
              key={listing.productExternalReference}
              listing={listing}
              actions={actions}
              mini={productListingsMiniView}
            />
          );
        })}
      </>
    );
  };

  return (
    <section className="dashboard" data-testid="dashboard">
      <section className="dashboard__left">
        <section className="dashboard-card dashboard__featured-listings">
          <section className="dashboard-card__header">
            <h2 className="dashboard-card__title">FEATURED LISTINGS</h2>
          </section>
          <section className="dashboard-card__content">{renderFeaturedListings()}</section>
        </section>

        <section className="dashboard-card dashboard__bookmarked-listings">
          <section className="dashboard-card__header">
            <h2 className="dashboard-card__title">BOOKMARKED LISTINGS</h2>
            <div className="dashboard-card__buttons">
              {bookmarkedListings.length > 0 && (
                <Button
                  className="dashboard-card__button"
                  onClick={() => setBookmarkedListingsMiniView(!bookmarkedListingsMiniView)}
                  secondary
                >
                  {bookmarkedListingsMiniView ? (
                    <SwitchViewIcon className="dashboard-card__button__icon" />
                  ) : (
                    <SwitchViewAlternateIcon className="dashboard-card__button__icon" />
                  )}
                  Switch View
                </Button>
              )}
            </div>
          </section>
          <section className="dashboard-card__content">{renderBookmarkedListings()}</section>
        </section>

        <section className="dashboard-card dashboard__product-listings">
          <section className="dashboard-card__header">
            <h2 className="dashboard-card__title">PRODUCT LISTINGS</h2>
            <div className="dashboard-card__buttons">
              {productListings.length > 0 && (
                <Button
                  className="dashboard-card__button"
                  onClick={() => setProductListingsMiniView(!productListingsMiniView)}
                  secondary
                >
                  {productListingsMiniView ? (
                    <SwitchViewIcon className="dashboard-card__button__icon" />
                  ) : (
                    <SwitchViewAlternateIcon className="dashboard-card__button__icon" />
                  )}
                  Switch View
                </Button>
              )}

              <Button className="dashboard-card__button" onClick={() => navigate('/market/product-listings')}>
                View All
              </Button>
            </div>
          </section>
          <section className="dashboard-card__content">{renderProductListings()}</section>
        </section>
      </section>

      <section className="dashboard__right">
        <section className="dashboard-card dashboard__market-buzz">
          <section className="dashboard-card__header">
            <h2 className="dashboard-card__title">MARKET BUZZ</h2>
            <div className="dashboard-card__buttons">
              <Button className="dashboard-card__button" onClick={() => navigate('/market/market-buzz')}>
                View All
              </Button>
            </div>
          </section>
          <section className="dashboard-card__content">
            {posts?.map((post) => (
              <PostContainer key={post.externalReference} post={post} reactions={reactions} adminRole={adminRole} />
            ))}
            {posts?.length === 0 && <p className="dashboard__empty-text">No Posts</p>}
            {posts?.length !== 0 && (
              <div className="market-buzz__load-more">
                {isLastPostLoaded ? <p>No more posts to load</p> : <Button onClick={viewPosts}>Load More</Button>}
              </div>
            )}
          </section>
        </section>
      </section>
    </section>
  );
};

Dashboard.propTypes = {
  featuredListings: PropTypes.arrayOf(PropTypes.shape({})),
  bookmarkedListings: PropTypes.arrayOf(PropTypes.shape({})),
  productListings: PropTypes.arrayOf(PropTypes.shape({})),
  posts: PropTypes.arrayOf(PropTypes.shape({})),
  reactions: PropTypes.arrayOf(PropTypes.shape({})),
  adminRole: PropTypes.bool,
  isLastPostLoaded: PropTypes.bool,
  updateBookmark: PropTypes.func.isRequired,
  viewPosts: PropTypes.func.isRequired,
  currencyCode: PropTypes.string.isRequired,
};

Dashboard.defaultProps = {
  featuredListings: [],
  bookmarkedListings: [],
  productListings: [],
  posts: [],
  reactions: [],
  adminRole: false,
  isLastPostLoaded: false,
};

export default Dashboard;
